/**
 * Shared constants and configuration.
 * Single source of truth for account definitions, formatting, and options.
 * Edit here → reflects across all pages automatically.
 */

// ===== Account Definitions =====

export interface AccountDef {
  slug: string
  name: string
  icon: string
  subtitle: string
}

export const ACCOUNTS: AccountDef[] = [
  { slug: 'wheel', name: 'Wheel (CSP/CC)', icon: '🎡', subtitle: 'CSP / CC' },
  { slug: 'amzn', name: 'AMZN (Covered Calls)', icon: '📦', subtitle: 'Covered Calls' },
  { slug: 'leap-calls', name: 'LEAP Calls', icon: '🚀', subtitle: 'Long calls' },
  { slug: 'leap-puts', name: 'LEAP Puts', icon: '🛡️', subtitle: 'Hedges' },
  { slug: 'richas-roth', name: "Richa's Roth", icon: '👩', subtitle: 'Roth IRA' },
  { slug: 'yogeshs-roth', name: "Yogesh's Roth", icon: '👨', subtitle: 'Roth IRA' },
  { slug: 'hsa', name: 'HSA', icon: '🏥', subtitle: 'Health savings' },
  { slug: 'spy-spread', name: 'SPY Spread', icon: '🦅', subtitle: 'Index spreads' },
  { slug: 'shares', name: 'Shares Account', icon: '🏦', subtitle: 'Long positions' },
]

/** Quick lookup: slug → display name */
export const ACCOUNT_NAMES: Record<string, string> = Object.fromEntries(
  ACCOUNTS.map(a => [a.slug, a.name])
)

// ===== Trade Action Options =====

export const TRADE_ACTIONS = [
  { label: 'CSP', value: 'CSP' },
  { label: 'CC', value: 'CC' },
  { label: 'Buy', value: 'Buy' },
] as const

export const CLOSE_ACTIONS = [
  { label: 'Buy Back', value: 'Buy Back' },
  { label: 'Expired Worthless', value: 'Expired Worthless' },
  { label: 'Assigned', value: 'Assigned' },
  { label: 'Sold', value: 'Sold' },
  { label: 'Rolled', value: 'Rolled' },
] as const

// ===== Formatting Utilities =====

/** Format a number as currency: $1,234.56 */
export function formatCurrency(n: number, decimals = 0): string {
  return '$' + n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** Format a date string (YYYY-MM-DD) as MM/DD/YY */
export function formatDateShort(val: string | number): string {
  const str = String(val)
  if (!str || str === '—') return '—'
  const parts = str.split('-')
  if (parts.length !== 3) return str
  const [year, month, day] = parts
  return `${month}/${day}/${year.slice(2)}`
}

/** Format a number as percentage: 12.34% */
export function formatPercent(n: number, decimals = 2): string {
  return `${n.toFixed(decimals)}%`
}

/** Format price with $ and 2 decimals, or — for zero/undefined */
export function formatPrice(val: number | undefined): string {
  if (val === undefined || val === 0) return '—'
  return '$' + val.toFixed(2)
}

// ===== Nav Configuration =====

export const NAV_ITEMS = [
  { to: '/', label: 'Watchlist', icon: '👁️', subtitle: 'Stock universe & assessments' },
  { to: '/market-data', label: 'Market Data', icon: '📊', subtitle: 'Data status & cache' },
  { to: '/portfolio', label: 'Portfolio', icon: '💰', subtitle: 'Budget & monthly P&L' },
  ...ACCOUNTS.filter(a => a.slug !== 'shares').map(a => ({
    to: `/trades/${a.slug}`,
    label: a.name.split(' (')[0], // Short label (e.g., "Wheel" not "Wheel (CSP/CC)")
    icon: a.icon,
    subtitle: a.subtitle,
  })),
  { to: '/projections', label: 'Projections', icon: '🔮', subtitle: 'Compound growth' },
  { to: '/report', label: 'Report Reader', icon: '📄', subtitle: 'View MD reports' },
  { to: '/import', label: 'Import', icon: '📥', subtitle: 'CSV / JSON upload' },
  { to: '/trades/shares', label: 'Shares', icon: '🏦', subtitle: 'Long positions' },
]

// ===== Default "More" section slugs =====
export const DEFAULT_MORE_SLUGS = ['/import', '/trades/shares', '/trades/spy-spread']
